import * as React from 'react';
import { FormControl, InputLabel, InputLabelProps as MuiInputLabelProps } from '@material-ui/core';
import { SelectProps as MuiSelectProps } from '@material-ui/core/Select';
import { Qualifier, Select, Typography } from 'bento';
import { Theme } from '@emotion/react';
import {
  selectFieldHelperText,
  selectFieldLabel,
  selectFieldRoot,
  selectFormControl,
} from './selectFieldCssDefs';
import { useMemo } from 'react';

export interface SelectFieldProps {
  className?: string;
  disabled?: boolean;
  label?: React.ReactNode;
  fullWidth?: boolean;
  helperText?: string;
  InputLabelProps?: MuiInputLabelProps;
  error?: boolean;
  SelectProps?: MuiSelectProps;
  children: React.ReactNode;
  name?: string;
  id?: string;
  value?: string | string[];
  SelectDisplayProps?: object;
  onChange?: (
    event: React.ChangeEvent<{ name?: string; value: unknown }>,
    child: React.ReactNode
  ) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onBlur?: React.FocusEventHandler<any>;
  qualifier?: Qualifier;
}

/**
 * Use `SelectField` for rendering an input where you can choose one of multiple choices, see stories for more complete examples. This is a wrapper for Select
 * that includes FormControl, InputLabel, and FormHelperText
 *
 * ```js
 * import { SelectField } from 'bento';
 * ```
 * @deprecated Deprecated — use pantry Select instead.
 * @see https://docs.tocktix.com/pantry?path=/docs/components-select--docs
 * Migration: Replace SelectField with the pantry Select component.
 */
export const SelectField: React.FunctionComponent<React.PropsWithChildren<SelectFieldProps>> = ({
  label,
  disabled,
  fullWidth,
  SelectProps,
  children,
  helperText,
  InputLabelProps = {},
  error,
  className,
  name,
  id,
  value,
  onChange,
  onBlur,
  qualifier = 'default',
  ...rest
}) => {
  const inputLabel = React.useRef<HTMLLabelElement>(null);

  const selectId = useMemo(() => id ?? SelectProps?.inputProps?.id, [id, SelectProps]);

  const describedBy = useMemo(() => {
    const ids = [];
    if (helperText) {
      ids.push(`${selectId}-helper-text`);
    }
    if (SelectProps?.inputProps?.['aria-describedby']) {
      ids.push(SelectProps?.inputProps?.['aria-describedby']);
    }
    return ids.join(' ') || undefined;
  }, [helperText, selectId, SelectProps]);

  return (
    <FormControl
      className={className}
      css={selectFormControl(fullWidth, qualifier, disabled)}
      variant="outlined"
      error={error}
      disabled={disabled}
      required={qualifier === 'required'}
    >
      {label && (
        <InputLabel
          {...InputLabelProps}
          css={selectFieldLabel(disabled)}
          ref={inputLabel}
          htmlFor={selectId}
        >
          {label}
        </InputLabel>
      )}
      <Select
        fullWidth={fullWidth}
        css={selectFieldRoot(disabled)}
        {...SelectProps}
        inputProps={{ ...SelectProps?.inputProps, 'aria-describedby': describedBy }}
        disabled={disabled ?? SelectProps?.disabled}
        name={name ?? SelectProps?.inputProps?.name}
        id={selectId}
        value={value ?? SelectProps?.value}
        onChange={onChange ?? SelectProps?.onChange}
        onBlur={onBlur ?? SelectProps?.onBlur}
        {...rest}
      >
        {children}
      </Select>
      {helperText && (
        <Typography
          id={`${selectId}-helper-text`}
          variant="caption"
          css={(theme: Theme) => selectFieldHelperText(theme, disabled, error)}
        >
          {helperText}
        </Typography>
      )}
    </FormControl>
  );
};
